import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import i18next from 'i18next';
import clsx from 'clsx';
import dayjs from 'dayjs';

import axiosInstance from '../../config/axiosConfig';
import { permissions, calculatePermissions } from '../../Utils/PermissionTags';

import useSelectedCompanyStore from '../../store/useSelectedCompanyStore';

import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { Sidebar } from 'primereact/sidebar';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Paginator } from 'primereact/paginator';
import { Tooltip } from 'primereact/tooltip';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';

const t = (i18nKey, params) => i18next.t(`userDashboard.company.${i18nKey}`, params);

const Members = () => {
    const [loading, setLoading] = useState(true);
    const [members, setMembers] = useState([]);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [invitationLoading, setInvitationLoading] = useState(false);
    const [invitations, setInvitations] = useState([]);
    const [newInvitation, setNewInvitation] = useState({ invited_email: '', bitfield: 0, max_uses: 1, exclusive: false });

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const toast = useRef(null);

    const fetchData = useCallback(async () => {
        if (!selectedCompany) return;

        setLoading(true);

        try {
            const response = await axiosInstance.get(`/dashboard/companies/${selectedCompany.company_id}/users/list`);
            if (!response?.data?.items || !response.data.items.length) return setLoading(false);

            const fetchedMembers = response.data.items.map((item) => ({
                user_id: item.user_id,
                permissions: Array.isArray(item.permissions) ? item.permissions : [],
                created_at: dayjs(item.created_at).format('DD/MM/YYYY - HH:mm'),
                first_name: item.first_name,
                last_name: item.last_name,
                profile_picture: item.profile_picture,
                invitation_code: item.invitation_code,
            }));

            setMembers(fetchedMembers);

        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la récupération des membres, veuillez réessayer plus tard', life: 10000 });
        } finally {
            setLoading(false);
        }
    }, [selectedCompany]);

    const handlePermissionSelect = (bit) => {
        setNewInvitation((prev) => {
            const newBitfield = prev.bitfield ^ bit;
            return { ...prev, bitfield: newBitfield };
        });
    };

    const fetchInvitations = useCallback(() => {
        if (!selectedCompany) return;

        setInvitationLoading(true);

        axiosInstance.get(`/dashboard/companies/${selectedCompany.company_id}/invitations/list`).then(response => {
            let fetchedInvitations = [];

            if (response.data?.items?.length > 0) {
                fetchedInvitations = response.data.items.map(invitation => ({
                    ...invitation,
                    // permissions: calculatePermissions(invitation.bitfield)
                }));
            }

            setInvitations(fetchedInvitations);
        }).catch(error => {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la récupération des invitations.', life: 3000 });
        }).finally(() => {
            setInvitationLoading(false);
        });
    }, [selectedCompany]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenSidebar = () => {
        fetchInvitations();
        setSidebarVisible(true);
    };

    const handleCloseSidebar = () => {
        setSidebarVisible(false);
    };

    const saveNewInvitation = () => {
        const { invited_email, bitfield, max_uses } = newInvitation;

        axiosInstance.post(`/dashboard/companies/${selectedCompany?.company_id}/invitations/invite`, { invited_email, bitfield, max_uses }).then(() => {
            fetchInvitations();
            setNewInvitation({ invited_email: '', bitfield: 0, max_uses: 1 });
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Invitation créée avec succès', life: 3000 });
        }).catch(error => {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: "Une erreur est survenue pendant la création de l'invitation", life: 3000 });
        });
    };

    const deleteInvitation = async (invitation_id) => {
        try {
            await axiosInstance.delete(`/dashboard/companies/${selectedCompany.company_id}/invitations/${invitation_id}/delete`);
            fetchInvitations();
            toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Invitation supprimée avec succès', life: 3000 });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Une erreur est survenue pendant la suppression de cette invitation', life: 3000 });
        };
    };

    const copyLink = async (invitation_code) => {
        const link = `${window.location.origin}/dashboard/?invitation_code=${invitation_code}`;
        if (!navigator.clipboard) {
            const textarea = document.createElement('textarea');
            textarea.value = link;
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                toast.current.show({ severity: 'info', summary: 'Lien copié', detail: 'Le lien de l\'invitation a été copié dans le presse-papiers.', life: 3000 });
            } catch (error) {
                console.error(error);
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la copie du lien.', life: 3000 });
            }
            document.body.removeChild(textarea);
        } else {
            try {
                await navigator.clipboard.writeText(link);
                toast.current.show({ severity: 'info', summary: 'Lien copié', detail: 'Le lien de l\'invitation a été copié dans le presse-papiers.', life: 3000 });
            } catch (error) {
                console.error(error);
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la copie du lien.', life: 3000 });
            }
        }
    };

    useEffect(() => {
        if (newInvitation?.max_uses <= 0) setNewInvitation({ max_uses: 1, invited_email: newInvitation?.invited_email, bitfield: 0 });
        else if (newInvitation?.max_uses > 999) setNewInvitation({ max_uses: 999, invited_email: newInvitation?.invited_email, bitfield: 0 });
    }, [newInvitation]);

    const memberTemplate = (rowData) => (
        <div className="p-grid p-align-center">
            <div className="p-col">
                <span className='is-capitalized' style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{rowData?.first_name}</span>

                <span className='is-capitalized ml-1' style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{rowData?.last_name}</span>
            </div>
        </div>
    );

    const permissionsTemplate = (rowData) => {
        if (loading) return <Skeleton width="100%" />;

        if (!Array.isArray(rowData.permissions)) {
            return <span>No permissions available</span>;
        }

        return rowData.permissions.map((permission, index) => (
            <span key={index} className="tag mr-2">{permission}</span>
        ));
    };

    const invitationCodeTemplate = (rowData) => {
        if (loading) return <Skeleton width="100%" />;

        return (
            <div className="tags has-addons mt-0" style={{ gap: 0 }}>
                <a className="tag"><span className="icon"><i className="fa-solid fa-link"></i></span></a>
                <span className="tag">{rowData?.invitation_code || 'Non trouvé'}</span>
            </div>
        )
    };

    const generateSkeletonData = useMemo(() => {
        return Array.from({ length: 10 }, (_, index) => ({
            user_id: index,
            first_name: <Skeleton width="100%" />,
            last_name: '',
            created_at: <Skeleton width="100%" />,
            invitation_code: <Skeleton width="100%" />
        }));
    }, []);

    const displayData = loading ? generateSkeletonData : members;

    return (
        <section className='home'>
            <Toast ref={toast} position={clsx({ 'bottom-right': !sidebarVisible, 'bottom-center': sidebarVisible })} />

            <div className="container">

                <div className="columns is-vcentered is-multiline">
                    <div className="column">
                        <p className="title is-3">
                            {t('tabs.members')}
                        </p>
                    </div>
                    <div className="column has-text-right">
                        <a className={clsx('button is-info', { 'is-unselectable': invitationLoading })} onClick={handleOpenSidebar} disabled={invitationLoading}>
                            <span className="icon"><i className="fas fa-plus-circle"></i></span>
                            <span>{t('members.member_invitation.invite_members')}</span>
                        </a>
                    </div>
                </div>

                <DataTable
                    dataKey="user_id"
                    stripedRows
                    value={displayData}>

                    <Column key="profile_picture" field="profile_picture" header="" body={(rowData) => (
                        <>
                            {!loading &&
                                <div className="is-flex is-align-items-center">
                                    {rowData.profile_picture ? (
                                        <Avatar
                                            image={`/dashboard/user/avatar/${rowData?.profile_picture}`}
                                            className="mr-3 my-1"
                                            size="large"
                                            shape="circle"
                                        />
                                    ) : (
                                        <div className="deepsy-anonymous-avatar mr-3 my-1">
                                            <span className='has-text-dgrad-deepsy'>{rowData?.first_name[0].toUpperCase()}{rowData.last_name[0].toUpperCase()}</span>
                                        </div>
                                    )}

                                </div>
                            }
                        </>
                    )} />

                    <Column key="member" sortable field="first_name" header={t('members.members_table.0')} body={memberTemplate} />
                    <Column key="invitation_code" field="invitation_code" header={t('members.members_table.1')} body={invitationCodeTemplate} />
                    <Column key="permissions" sortable field="permissions" header={t('members.members_table.2')} body={permissionsTemplate} />
                    <Column key="created_at" sortable field="created_at" header={t('members.members_table.3')} />
                </DataTable>

                <Sidebar visible={sidebarVisible} position="right" onHide={handleCloseSidebar} className='is-width-8'>
                    <h1 className="title">{t('members.member_invitation.invite_members')}</h1>

                    <hr className="is-invisible" />

                    <Card title={t('members.member_invitation.new_invitation')}>

                        <div className="field">
                            <label htmlFor="max_uses" className="label">{t('members.member_invitation.max_uses')}</label>
                            <div className="control">
                                <div className="flex-auto">
                                    <InputNumber
                                        inputId="max_uses"
                                        value={newInvitation.max_uses}
                                        onValueChange={(e) => setNewInvitation({ ...newInvitation, max_uses: e.value })}
                                        mode="decimal"
                                        min={1} max={999}
                                        showButtons
                                        readOnly={newInvitation.exclusive}
                                        disabled={newInvitation.exclusive}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="field">
                            <label htmlFor="invited_email" className="label">{t('members.member_invitation.invitation_email')}</label>
                            <div className="control">
                                <InputText
                                    id="invited_email"
                                    type="email"
                                    value={newInvitation.invited_email}
                                    onChange={(e) => setNewInvitation({ ...newInvitation, invited_email: e.target.value })}
                                    className="input"
                                />
                            </div>
                        </div>

                        <div className="field">
                            <label htmlFor="permissions" className="label">{t('members.member_invitation.permissions')}</label>
                            <div className="control">
                                <div className="tags" style={{ justifyContent: 'unset' }}>
                                    {Object.entries(permissions).slice(1).map(([bit, permission]) => (
                                        <span
                                            key={bit}
                                            className={clsx('tag is-clickable', { 'is-info': (newInvitation.bitfield & bit) !== 0 })}
                                            onClick={() => handlePermissionSelect(Number(bit))}
                                        >{permission}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="field">
                            <div className="control">
                                <label className="checkbox">
                                    <input
                                        type="checkbox"
                                        checked={newInvitation.exclusive}
                                        onChange={(e) => setNewInvitation({ ...newInvitation, exclusive: e.target.checked })}
                                    /> {t('members.member_invitation.exclusive')}
                                </label>
                            </div>
                        </div>

                        <div className="field mt-2">
                            <a
                                className={clsx('button is-info is-light is-outlined', { 'is-disabled': newInvitation.permission === 0 })}
                                onClick={saveNewInvitation}
                                disabled={newInvitation.permission === 0}
                            >
                                <span className="icon">
                                    <i className="fa-solid fa-check"></i>
                                </span>
                                <span>{t('members.member_invitation.save')}</span>
                            </a>
                        </div>
                    </Card>

                    <hr className="is-invisible" />

                    <Card title={t('members.member_invitation.invitations')}>
                        {invitationLoading ? (
                            <Skeleton width="100%" height="2rem" />
                        ) : invitations.length > 0 ? (
                            <ul>
                                {invitations.map((invitation) => (
                                    <div key={invitation.invitation_code} className="box is-flex is-justify-content-space-between is-align-items-center mb-2">
                                        <div>

                                            <strong>{invitation?.invited_email || t('members.member_invitation.no_email')}</strong>
                                            <p>{t('members.member_invitation.code')}: {invitation.invitation_code}</p>
                                            <p>{t('members.member_invitation.uses')}: {invitation.used_count} / {invitation.max_uses}</p>

                                            <div className="tags" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', justifyContent: 'unset' }}>
                                                {calculatePermissions(invitation.permissions).map((permission, index) => (
                                                    <span key={index} className="tag is-success is-light is-clickable">{permission}</span>
                                                ))}
                                            </div>

                                            <Button
                                                icon="pi pi-copy"
                                                className="p-button-rounded p-button-info p-mr-2"
                                                onClick={() => copyLink(invitation.invitation_code)}
                                                tooltipOptions={{ position: 'left', mouseTrack: true, mouseTrackLeft: 10 }}
                                                tooltip={t('members.member_invitation.copy_link', { link: `${window.location.origin}/dashboard/?invitation_code=${invitation.invitation_code}` })}
                                            />
                                        </div>

                                        <Button
                                            label={t('members.member_invitation.delete')}
                                            icon="pi pi-trash"
                                            className="button is-danger is-rounded"
                                            onClick={() => deleteInvitation(invitation.invitation_code)}
                                        />
                                    </div>
                                ))}
                            </ul>
                        ) : (
                            <p>{t('members.member_invitation.no_invitations')}</p>
                        )}
                    </Card>
                </Sidebar>

            </div>
        </section>
    );
};

export default Members;